// src/allergies.js
//
// Shared allergy data + helpers. Imported by MemberProfile (CRUD) and
// ChefDashboard (lookup + pill styling).
//
// Schema lives in the Google Sheet `AllergyProfiles`:
//   id                 — unique row id (we generate)
//   hostMemberNumber   — the responsible member's number (string)
//   name               — display name of the eater (member, spouse, child, guest)
//   role               — one of: self | spouse | child | guest
//   allergies          — comma-separated chip values (see CHIP_OPTIONS)
//   note               — freeform notes for the chef (severity, cross-contact, brands)
//   severity           — one of: none | mild | severe
//   addedAt            — ISO timestamp
//
// Backward compat: the old Members sheet `allergy` and `allergyNote` columns
// are still respected as a *fallback* by chef lookup until the member opens
// their profile and the legacy data is migrated into AllergyProfiles as a
// `self` row. After migration the legacy columns are no longer used.

// Expanded chip set. "Other" is intentionally last; the always-visible note
// field is the structured place for anything not on this list.
export const CHIP_OPTIONS = [
  'Alpha Gal',
  'Gluten',
  'Wheat',
  'Dairy',
  'Lactose',
  'Eggs',
  'Peanuts',
  'Tree Nuts',
  'Soybeans',
  'Shellfish',
  'Fish',
  'Sesame',
  'Sulfites',
  'Corn',
  'MSG',
  'Onions/Garlic',
  'Other',
];

export const ROLE_OPTIONS = [
  { value: 'self', label: 'Member (you)' },
  { value: 'spouse', label: 'Spouse / partner' },
  { value: 'child', label: 'Child' },
  { value: 'guest', label: 'Guest' },
];

export const SEVERITY_OPTIONS = [
  { value: 'none', label: 'No special severity' },
  { value: 'mild', label: 'Mild — preference / discomfort' },
  { value: 'severe', label: 'Severe — life-threatening / anaphylaxis' },
];

export const roleLabel = (r) => {
  const x = (r || '').toString().trim().toLowerCase();
  const found = ROLE_OPTIONS.find((o) => o.value === x);
  return found ? found.label : 'Member';
};

export const severityLabel = (s) => {
  const x = (s || '').toString().trim().toLowerCase();
  const found = SEVERITY_OPTIONS.find((o) => o.value === x);
  return found ? found.label : 'No special severity';
};

// Returns CSS-friendly color tokens for a chef pill given severity.
// Yellow = mild (default), red = severe, neutral if no allergies at all.
export const severityPillStyle = (severity, dark) => {
  const sev = (severity || 'mild').toLowerCase();
  if (sev === 'severe') {
    return {
      borderColor: dark ? 'rgba(239,68,68,0.6)' : 'rgba(220,38,38,0.7)',
      background: dark ? 'rgba(239,68,68,0.18)' : 'rgba(254,202,202,0.7)',
      color: dark ? '#fecaca' : '#7f1d1d',
      fontWeight: 800,
    };
  }
  // mild / none → existing yellow
  return {
    borderColor: dark ? 'rgba(253, 224, 71, 0.45)' : 'rgba(146, 64, 14, 0.35)',
    background: dark ? 'rgba(250, 204, 21, 0.18)' : 'rgba(254, 243, 199, 0.85)',
    color: dark ? '#fde68a' : '#7c2d12',
    fontWeight: 700,
  };
};

export const parseChips = (s) =>
  (s || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

export const serializeChips = (arr) => (arr || []).join(', ');

// Normalize a person name for matching: lowercase, collapse whitespace,
// strip booking tags `(c)`, `(child)`, `(g)`, `(guest)`.
export const normalizePersonName = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/\(c(hild)?\)/gi, '')
    .replace(/\(g(uest)?\)/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

// Build a chef-friendly summary string for a profile row.
// e.g. "Gluten, Sesame (cross-contact ok)"
export const summaryString = (allergies, note) => {
  const a = (allergies || '').toString().trim();
  const n = (note || '').toString().trim();
  if (a && n) return `${a} — ${n}`;
  if (a) return a;
  if (n) return n;
  return '';
};

export const makeProfileId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return `ap_${crypto.randomUUID().slice(0, 18)}`;
    }
  } catch {}
  return `ap_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

export const nowIso = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const offMin = -d.getTimezoneOffset();
  const sign = offMin >= 0 ? '+' : '-';
  const abs = Math.abs(offMin);
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
  );
};
